import React, { useState } from 'react';
import { Download, ListChecks } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getCellValue } from '../../utils/candidateProgramTools';
import type { OdrTaskCandidateRow } from './OdrCandidateTaskDialog';

// Column order and spelling come from the court team's checklist template.
export const COURT_CHECKLIST_HEADERS = [
  'Seen By', 'Date', 'Dept', 'Existing Client New Case (Y/N)', 'Hearing Type',
  'Name', 'Booking', 'Last 4 FIN # (Only Last 4!)', 'Case(s)', 'Outcome',
  'Chronic Homelessness', 'LOC', 'FSP Priority (Y/N)', 'Medical Needs',
  'Notes', 'Release After', 'Holds',
];

type CourtHearing = { caseNumber: string; date: string; department: string; hearingType: string };
type CourtChecklist = { rows: string[][]; unmatched: number; ambiguous: number };
const normalizeCaseNumber = (value: string): string => value.toUpperCase().replace(/[^A-Z0-9]/g, '');

// Read only the calendar columns needed for the checklist, retaining Excel's displayed dates.
export const parseCourtCalendar = (bytes: ArrayBuffer): CourtHearing[] => {
  const workbook = XLSX.read(bytes, { type: 'array', dateNF: 'mm/dd/yyyy' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) throw new Error('The file has no worksheet.');
  const rows = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, raw: false, defval: '' });
  const headers = (rows[0] || []).map((value) => value.trim().toLowerCase());
  const required = ['Case Number', 'Hearing Date', 'Department', 'Hearing Type'];
  const missing = required.filter((label) => !headers.includes(label.toLowerCase()));
  if (missing.length) throw new Error(`Missing columns: ${missing.join(', ')}.`);
  const indexes = required.map((label) => headers.indexOf(label.toLowerCase()));
  const hearings: CourtHearing[] = [];
  for (const row of rows.slice(1)) {
    if (row.every((value) => !value.trim())) continue;
    hearings.push({
      caseNumber: (row[indexes[0]] || '').trim(),
      date: (row[indexes[1]] || '').trim(),
      department: (row[indexes[2]] || '').trim(),
      hearingType: (row[indexes[3]] || '').trim(),
    });
  }
  if (!hearings.length) throw new Error('The court list has no hearings.');
  return hearings;
};

// A case belonging to different candidates is ambiguous; never guess from the court's names.
export const buildCourtChecklist = (hearings: CourtHearing[], candidates: OdrTaskCandidateRow[]): CourtChecklist => {
  const byCase = new Map<string, OdrTaskCandidateRow | null>();
  for (const candidate of candidates) {
    const cases = getCellValue(candidate, 'n_case').split(/[,;\r\n|]+|\s+\/\s+/);
    for (const caseNumber of cases) {
      const key = normalizeCaseNumber(caseNumber);
      if (!key) continue;
      if (!byCase.has(key)) {
        byCase.set(key, candidate);
        continue;
      }
      if (String(byCase.get(key)?.id) !== String(candidate.id)) byCase.set(key, null);
    }
  }
  const report: CourtChecklist = { rows: [], unmatched: 0, ambiguous: 0 };
  for (const hearing of hearings) {
    const candidate = byCase.get(normalizeCaseNumber(hearing.caseNumber));
    if (candidate === undefined) {
      report.unmatched += 1;
      continue;
    }
    if (candidate === null) {
      report.ambiguous += 1;
      continue;
    }
    // Each court hearing remains a row. Seen By, new-case status and outcomes are entered in court.
    report.rows.push([
      '', hearing.date, hearing.department, '', hearing.hearingType,
      getCellValue(candidate, 'name'), getCellValue(candidate, 'n_bk'),
      getCellValue(candidate, 'main_last_four').replace(/\D/g, '').slice(-4),
      getCellValue(candidate, 'n_case'), '', '', '', '', '', '', '', '',
    ]);
  }
  return report;
};

// Strings keep booking/case numbers and FIN zeros intact and never become spreadsheet formulas.
export const createCourtChecklistWorkbook = (report: CourtChecklist): ArrayBuffer => {
  const sheet = XLSX.utils.aoa_to_sheet([
    ['Court Checklist'], COURT_CHECKLIST_HEADERS, ...report.rows,
  ]);
  sheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COURT_CHECKLIST_HEADERS.length - 1 } }];
  sheet['!cols'] = [14, 12, 9, 18, 22, 26, 14, 15, 24, 22, 18, 10, 15, 22, 32, 16, 18].map((wch) => ({ wch }));
  sheet['!rows'] = [{ hpt: 24 }, { hpt: 32 }, ...report.rows.map(() => ({ hpt: 48 }))];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Court Checklist');
  return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
};

type OdrCourtChecklistDialogProps = {
  onClose: () => void;
  loadCandidates: () => Promise<OdrTaskCandidateRow[]>;
};

// The parent supplies the established paginated candidate query; the court file stays in this dialog.
export const OdrCourtChecklistDialog = ({ onClose, loadCandidates }: OdrCourtChecklistDialogProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<CourtChecklist | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const reviewList = async () => {
    if (!file || busy) return;
    setError('');
    setReport(null);
    if (!/\.xlsx$/i.test(file.name)) {
      setError('Choose an Excel (.xlsx) court list.');
      return;
    }
    setBusy(true);
    try {
      const bytes = await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (!(reader.result instanceof ArrayBuffer)) {
            reject(new Error('The file could not be read. Choose it again.'));
            return;
          }
          resolve(reader.result);
        };
        reader.onerror = () => reject(new Error('The file could not be read. Choose it again.'));
        reader.readAsArrayBuffer(file);
      });
      let hearings: CourtHearing[];
      try {
        hearings = parseCourtCalendar(bytes);
      } catch (parseError: unknown) {
        setError(parseError instanceof Error && /^(Missing columns:|The file has no worksheet\.|The court list has no hearings\.)/.test(parseError.message)
          ? parseError.message : 'The file could not be read. Choose a valid Excel court list.');
        return;
      }
      const candidates = await loadCandidates();
      setReport(buildCourtChecklist(hearings, candidates));
    } catch (loadError: unknown) {
      setError(loadError instanceof Error && loadError.message === 'The file could not be read. Choose it again.'
        ? loadError.message : 'The candidates could not be loaded. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const downloadChecklist = () => {
    if (!report?.rows.length) return;
    setError('');
    try {
      const bytes = createCourtChecklistWorkbook(report);
      saveAs(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'Court Checklist.xlsx');
    } catch {
      setError('The checklist could not be downloaded. Try again.');
    }
  };

  return (
    <Dialog open onOpenChange={(open: boolean) => { if (!open && !busy) onClose(); }}>
      <DialogContent contentWidth="560px" contentMaxWidth="90vw" contentHeight="auto" className="flex flex-col gap-0 overflow-hidden p-0" aria-describedby={undefined}>
        <DialogHeader className="shrink-0 border-b border-[var(--neutral-gray-40)] p-4 pr-10">
          <DialogTitle>Court Checklist</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 overflow-y-auto p-4" aria-busy={busy}>
          <label htmlFor="odr-court-checklist-upload" className="mb-1 block text-sm font-medium">Court list (.xlsx)</label>
          <Input
            id="odr-court-checklist-upload"
            type="file"
            accept=".xlsx"
            disabled={busy}
            onChange={(event) => { setFile(event.target.files?.[0] || null); setReport(null); setError(''); }}
          />
          {busy && <p role="status" className="mt-3 text-sm">Comparing court list…</p>}
          {report && (
            <div role="status" className="mt-3 text-sm">
              <p>Matched hearings: {report.rows.length}</p>
              <p>Excluded: {report.unmatched} unmatched, {report.ambiguous} ambiguous.</p>
              {!report.rows.length && <p className="mt-2">No court entries matched a candidate.</p>}
            </div>
          )}
          {error && <p role="alert" className="mt-3 text-sm text-[var(--semantic-error)]">{error}</p>}
        </div>
        <DialogFooter className="shrink-0 border-t border-[var(--neutral-gray-40)] p-4">
          <Button id="odr-court-checklist-review" type="button" variant="outline" size="sm" disabled={!file || busy} onClick={() => void reviewList()}>
            <ListChecks className="h-4 w-4" />Review List
          </Button>
          <Button id="odr-court-checklist-download" type="button" size="sm" disabled={!report?.rows.length || busy} onClick={downloadChecklist}>
            <Download className="h-4 w-4" />Download
          </Button>
          <Button id="odr-court-checklist-close" type="button" variant="outline" size="sm" disabled={busy} onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
